import { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import CustomModal from '../customModal';
import SelectField from '../selectField';
import VirtualizedList from '../virtualizedList';
import ManageUserTagsDialog from '../manageUserTagsDialog';
import User from './user';
import {
  isArrayEmpty,
  isObjectEmpty,
  getObjectToNumberArray,
} from '../../../utility/helpers';
import { useAvailableUsers } from '../../../utility/hooks';
import { validateFields } from '../../../utility/validation';
import { USER_STATUSES } from '../../../constants/statuses';
import { PARAMS } from '../../../constants/pages';
import {
  USER_BASIC_PERSONAL_INFORMATION,
  USER_EMPLOYMENT_INFORMATION,
} from '../../../constants/people';
import { BULK_ACTION_OPTIONS_VALUES } from '../../../constants/bulkActionOptions';
import { fieldsToShow, prepareUsersToHandle } from './config';

const useStyles = makeStyles(({ spacing }) => ({
  usersWrapper: {
    marginBottom: spacing(4),
  },

  usersViewport: {
    maxHeight: 186,
  },
  fields: {
    position: 'relative',
  },
  field: {
    marginBottom: spacing(6),
  },
  hasError: {
    marginBottom: spacing(1),
  },
  inviteMessage: {
    marginBottom: spacing(4),
  },
  usersToInvite: {
    marginTop: spacing(1),
  },
  scrollY: {
    right: -19,
  },
}));

const BulkEditDialog = props => {
  const {
    translations,
    isOpened,
    selectedBulkAction,
    organizationUser,
    users,
    idleUsers,
    categories,
    onCancel,
    onSave,
  } = props;
  const { ACTIVE_INVITED } = USER_STATUSES;
  const classes = useStyles();

  const [selected, setSelected] = useState({
    jobTitle: null,
    reportTo: null,
    framework: null,
    track: null,
    level: null,
    division: null,
    department: null,
  });
  const [errors, setErrors] = useState({});

  const isChangeReport = useMemo(
    () =>
      selectedBulkAction.value === BULK_ACTION_OPTIONS_VALUES.changeReportingTo,
    [selectedBulkAction]
  );

  const params = useMemo(() => {
    if (!isOpened) return {};

    return isChangeReport && !isArrayEmpty(users)
      ? {
          [PARAMS.AVAILABLE_MANAGERS]: getObjectToNumberArray(users),
        }
      : {};
  }, [isOpened, users, isChangeReport]);

  const availableManagers = useAvailableUsers(params, organizationUser, true);

  const isInviteDisabled = useMemo(() => isArrayEmpty(idleUsers), [idleUsers]);
  const isInvite = useMemo(
    () => isArrayEmpty(fieldsToShow[selectedBulkAction.value]),
    [selectedBulkAction]
  );

  const getVirtualUserSize = useCallback(() => 24, []);

  const getOptionsByField = useCallback(
    field => {
      if (field.options) {
        if (field.parent) {
          const parentValue = selected[field.parent.name];
          if (!parentValue) {
            return [];
          }
          const parent = props[field.parent.options].find(
            elem => elem.id === parentValue
          );

          return (parent && parent[field.options]) || [];
        }
        return props[field.options] || [];
      }
      return [];
    },
    [props, selected]
  );

  const resetDependants = fieldDependants => {
    if (fieldDependants && !isArrayEmpty(fieldDependants)) {
      const dependantsValues = fieldDependants.reduce((acc, dependant) => {
        return { ...acc, [dependant.name]: dependant.value };
      }, {});

      return setSelected(prevState => ({
        ...prevState,
        ...dependantsValues,
      }));
    }
  };

  const resetState = () => {
    setSelected(prevSelected => ({
      ...prevSelected,
      ...{
        jobTitle: null,
        reportTo: null,
        framework: null,
        track: null,
        level: null,
        division: null,
        department: null,
      },
    }));
    setErrors({});
  };

  const onCloseDialog = () => {
    resetState();
    onCancel();
  };

  const handleSaveTags = ({ tags }) => {
    if (isArrayEmpty(tags)) {
      return onCloseDialog();
    }

    const postData = users.reduce((acc, user) => {
      return [
        ...acc,
        {
          id: user.id,
          ...(tags ? { tags } : {}),
        },
      ];
    }, []);

    onSave(postData);
  };

  const handleSelectValue = (name, dependants) => value => {
    const updatedErrors = { ...errors };

    if (errors && errors[name]) {
      updatedErrors[name] = null;
    }

    setSelected(prevSelected => ({
      ...prevSelected,
      [name]: value,
    }));
    resetDependants(dependants);
    setErrors(prevErrors => ({ ...prevErrors, ...updatedErrors }));
  };

  const validateAndSubmit = async () => {
    const { STATUS } = USER_BASIC_PERSONAL_INFORMATION;
    const { JOB_TITLE, REPORT_TO } = USER_EMPLOYMENT_INFORMATION;
    const {
      jobTitle,
      reportTo,
      framework,
      track,
      level,
      division,
      department,
    } = selected;
    const usersToHandle = prepareUsersToHandle(users, idleUsers, isInvite);
    let updatedErrors = {};

    const postData = usersToHandle.reduce((acc, user) => {
      return [
        ...acc,
        {
          id: user?.id,
          ...(jobTitle ? { [JOB_TITLE]: jobTitle } : {}),
          ...(reportTo ? { [REPORT_TO]: reportTo?.id } : {}),
          ...(isInvite ? { [STATUS]: ACTIVE_INVITED } : {}),
          ...(framework ? { framework } : {}),
          ...(track ? { track } : {}),
          ...(level ? { level } : {}),
          ...(division ? { division } : {}),
          ...(department ? { department } : {}),
        },
      ];
    }, []);

    updatedErrors = await validateFields(
      fieldsToShow[selectedBulkAction.value],
      selected
    );

    setErrors(updatedErrors);

    if (isObjectEmpty(updatedErrors)) {
      try {
        await onSave(postData);
      } finally {
        resetState();
      }
    }
  };

  const renderUser = useCallback(
    user => <User translations={translations} user={user} />,
    [translations]
  );

  const renderUsers = () => {
    const selectedUsers = isInvite ? idleUsers : users;

    return (
      !isArrayEmpty(selectedUsers) && (
        <div className={classes.usersWrapper}>
          <VirtualizedList
            viewportClass={classes.usersViewport}
            verticalScrollClass={classes.scrollY}
            items={selectedUsers}
            count={selectedUsers?.length ?? 0}
            gap={16}
            estimateSize={getVirtualUserSize}
            renderItem={renderUser}
          />
        </div>
      )
    );
  };

  const renderField = field => {
    const label = translations.fields[field.translationKey];
    const { placeholders } = translations.fields;
    const hasError = !!errors[field.name];
    const fieldClasses = classNames(classes.field, {
      [classes.hasError]: hasError,
    });
    const labelHelpData = field.labelHelp && {
      tooltipText: translations.labelHelpData[field.translationKey].text,
    };

    return (
      <div key={field.name} className={fieldClasses}>
        <SelectField
          errorClass={classes.error}
          name={field.name}
          label={label}
          placeholder={placeholders[field.translationKey]}
          labelHelp={labelHelpData}
          value={selected[field.name]}
          hasError={hasError}
          errorMessage={
            translations.fieldValidations[field.translationKey][
              errors[field.name]
            ] || ''
          }
          shouldReturnOption={field.shouldReturnOption}
          isUser={field.isUser}
          options={
            field.isReportTo ? availableManagers : getOptionsByField(field)
          }
          parser={field.parser}
          required={field.required}
          isDisabled={
            (field.parent && !selected[field.parent.name]) ||
            (!isChangeReport && isArrayEmpty(getOptionsByField(field)))
          }
          isSearchDisabled={field.isSearchDisabled}
          isClearable={field.isClearable}
          onChange={handleSelectValue(field.name, field.dependants)}
          isFullWidth
        />
      </div>
    );
  };

  const renderInviteUsersMessage = () => {
    const {
      following,
      invited,
      userText,
      usersText,
      userHas,
      usersHave,
      noInvitation,
    } = translations.inviteMessage;
    const numberOfUsersToInvite = idleUsers.length;
    const numberOfUsersNotToInvite = users.length - numberOfUsersToInvite;

    return (
      <div className={classes.inviteMessage}>
        <Typography>
          {numberOfUsersNotToInvite > 0 &&
            `${noInvitation}${numberOfUsersNotToInvite}${
              numberOfUsersNotToInvite === 1 ? userHas : usersHave
            }`}
        </Typography>
        <Typography className={classes.usersToInvite}>
          {numberOfUsersToInvite > 0 &&
            `${following}${
              numberOfUsersToInvite === 1 ? userText : usersText
            } ${invited}`}
        </Typography>
      </div>
    );
  };

  return selectedBulkAction.key === 'applyTags' ? (
    <ManageUserTagsDialog
      translations={translations}
      isOpened={isOpened}
      categories={categories}
      userAvatars={renderUsers()}
      onCancel={onCloseDialog}
      onSave={handleSaveTags}
    />
  ) : (
    <CustomModal
      title={translations[selectedBulkAction.key]}
      confirmButtonLabel={
        isInvite ? translations.inviteUsers : translations.save
      }
      closeButtonLabel={translations.cancel}
      isOpened={isOpened}
      isConfirmDisabled={isInvite && isInviteDisabled}
      onClose={onCloseDialog}
      onConfirm={validateAndSubmit}
      isMedium
    >
      {isInvite && <div>{renderInviteUsersMessage()}</div>}
      {renderUsers()}
      <div className={classes.fields}>
        {fieldsToShow[selectedBulkAction.value]?.map(renderField)}
      </div>
    </CustomModal>
  );
};

BulkEditDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  jobTitles: PropTypes.arrayOf(PropTypes.object).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  isOpened: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  organizationUser: PropTypes.shape({}).isRequired,
  selectedBulkAction: PropTypes.object.isRequired,
  idleUsers: PropTypes.array.isRequired,
};

export default BulkEditDialog;
